import { useCallback, useEffect, useState } from 'react'
import {
    DataTableCustomized,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { CustomerPreview } from '@/hooks/useCustomersPreviewFilter'
import {
    CreateSurveyRecordPayload,
    RecordResponse,
} from '@/modules/Survey/types'
import PrivacyField from '@/components/PrivacyField'
import { CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'
import { SCORE_CATEGORIES } from './SurveyExpandedRowContent'
import SurveyExpandedRowContent from './SurveyExpandedRowContent'

interface Props {
    customers: CustomerPreview[]
    responses: CreateSurveyRecordPayload[]
    onResponsesChanged: (payload: CreateSurveyRecordPayload) => void
    onRemoveCustomer?: (customerId: number) => void
}

const checkComplete = (
    id: number,
    responseList: CreateSurveyRecordPayload[]
) => {
    const r = responseList.find((r) => r.customer_id === id)
    return r && SCORE_CATEGORIES.every((cat) => (r.records?.[cat.key] || 0) > 0)
}

const SurveyResponseTableView = ({
    customers,
    responses,
    onResponsesChanged,
    onRemoveCustomer,
}: Props) => {
    const { t } = useTranslation('surveys')

    // Controlled expand state — auto-expand first incomplete row
    const [expandedKeys, setExpandedKeys] = useState<string[]>(() => {
        const first = customers[0]
        return first ? [String(first.customer_id)] : []
    })

    // When responses change, ensure first incomplete row stays expanded
    useEffect(() => {
        const firstIncomplete = customers.find(
            (c) => !checkComplete(c.customer_id, responses)
        )
        if (!firstIncomplete) return

        const key = String(firstIncomplete.customer_id)
        setExpandedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]))
    }, [customers])

    const updateRecord = useCallback(
        (id: number, patch: Partial<RecordResponse>) => {
            const payload = responses.find((r) => r.customer_id === id) || {
                customer_id: id,
                records: {
                    score_service_quality: 0,
                    score_customer_care: 0,
                    score_tech_support: 0,
                    score_issue_resolution: 0,
                    score_referral_willingness: 0,
                    upsale_potential: [],
                },
            }
            onResponsesChanged({
                ...payload,
                records: { ...payload.records, ...patch },
            })
        },
        [responses, onResponsesChanged]
    )

    const expandedRowRender = useCallback(
        (row: CustomerPreview) => {
            const response = responses.find(
                (r) => r.customer_id === row.customer_id
            )
            return (
                <SurveyExpandedRowContent
                    customerId={row.customer_id}
                    response={response}
                    onUpdate={updateRecord}
                />
            )
        },
        [responses, updateRecord]
    )

    const columns: DataTableColumn<CustomerPreview>[] = [
        {
            key: 'customer_id',
            title: t('customer', 'Customer'),
            render: (_value, row) => (
                <div className="flex items-center gap-1.5">
                    {checkComplete(row.customer_id, responses) && (
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-500" />
                    )}
                    <Link
                        to={generatePathDatacentral(paths.customerDetail, {
                            id: row.customer_id,
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline uppercase"
                    >
                        {row.customer_id} -{' '}
                        {row.company_name || row.customer_name}
                    </Link>
                </div>
            ),
        },
        {
            key: 'customer_name',
            title: t('contact_name', 'Contact'),
            render: (_value, row) => row.customer_name || '-',
        },
        {
            key: 'customer_phone',
            title: t('surveys.column.customer_phone', 'Phone'),
            render: (value) =>
                value ? (
                    <PrivacyField
                        value={String(value)}
                        type="phone"
                        toggleable
                    />
                ) : (
                    '-'
                ),
        },
        {
            key: 'service_groups',
            title: t('product_group', 'Service Group'),
            render: (_value, row) =>
                row.service_groups?.length
                    ? row.service_groups.join(', ')
                    : '-',
        },
    ]

    return (
        <DataTableCustomized<CustomerPreview>
            columns={columns}
            rows={customers}
            rowKey={(row) => row.customer_id}
            size="small"
            expandable={{
                expandedRowRender,
                expandedRowKeys: expandedKeys,
                onExpandedRowsChange: setExpandedKeys,
            }}
            actions={
                onRemoveCustomer
                    ? [
                          {
                              key: 'delete',
                              label: t('common.delete', 'Delete'),
                              variant: 'danger',
                              onClick: (row) =>
                                  onRemoveCustomer(row.customer_id),
                          },
                      ]
                    : []
            }
        />
    )
}

export default SurveyResponseTableView
